import { Link } from "wouter";
import { flatCoords, type Geometry } from "../lib/geo";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";

const POINT_ZOOM = 13;
const MIN_ZOOM = 3;
const MAX_ZOOM = 15;

/**
 * Camera centered on the geometry's bbox. The map page takes only
 * `lng`/`lat`/`z` params (no fitBounds on entry), so the zoom is a rough
 * fit: enough to frame the extent, clamped to sane bounds.
 */
function cameraFor(geom: Geometry | null | undefined): { lng: number; lat: number; zoom: number } | null {
  const coords = flatCoords(geom);
  if (!coords || coords.length === 0) return null;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
  // Latitude degrees cover roughly half the screen the same span of longitude
  // does at mid-latitudes, so weight them up when picking the framing span.
  const span = Math.max(maxLng - minLng, (maxLat - minLat) * 2);
  const zoom = span <= 0 ? POINT_ZOOM : Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.log2(360 / span) - 0.5));
  return { lng: (minLng + maxLng) / 2, lat: (minLat + maxLat) / 2, zoom };
}

/** Links to the main map page with the camera framing the given geometry. */
export function ViewOnMapButton({ geometry }: { geometry: Geometry | null | undefined }) {
  const { t } = useT();
  const camera = cameraFor(geometry);
  if (!camera) return null;
  const href = `/?lng=${camera.lng.toFixed(4)}&lat=${camera.lat.toFixed(4)}&z=${camera.zoom.toFixed(1)}`;
  return (
    <Button variant="outline" asChild>
      <Link href={href}>
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M8.157 2.176a1.5 1.5 0 0 0-1.147 0l-4.084 1.69A1.5 1.5 0 0 0 2 5.253v10.5a1.5 1.5 0 0 0 2.074 1.386l3.51-1.452 4.26 1.763a1.5 1.5 0 0 0 1.146 0l4.083-1.69A1.5 1.5 0 0 0 18 14.374v-10.5a1.5 1.5 0 0 0-2.073-1.386l-3.51 1.452-4.26-1.763ZM7.58 5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 7.58 5Zm5.59 2.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z"
            clipRule="evenodd"
          />
        </svg>
        {t("viewOnMap.button")}
      </Link>
    </Button>
  );
}
